/**
 * The theme preference store, and the one place that writes the class onto the DOM at
 * runtime.
 *
 * `public/theme-init.js` has already applied the correct class before first paint. This
 * store exists for what happens *after* that: the trainer flipping the preference, and the
 * OS flipping `prefers-color-scheme` while a `system` preference is active.
 *
 * The DOM and storage side effects live in `startThemeSync()`, called once from
 * `main.tsx` — not in the store's creation, so the store itself stays a plain state
 * container a test can drive without a document.
 */

import { create } from 'zustand';

import {
  ALL_THEME_CLASSES,
  THEME_STORAGE_KEY,
  parseThemePreference,
  resolveThemeClass,
  type ThemeClass,
  type ThemePreference,
} from './themeContract';
import { paintThemeOverrides } from './themeOverrides';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

interface ThemeState {
  preference: ThemePreference;
  systemPrefersDark: boolean;
  setPreference: (preference: ThemePreference) => void;
  /** Called by the media-query listener; not part of the UI's surface. */
  setSystemPrefersDark: (systemPrefersDark: boolean) => void;
}

function readStoredPreference(): ThemePreference {
  try {
    return parseThemePreference(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
}

function readSystemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_MEDIA_QUERY).matches
    : false;
}

export const useThemeStore = create<ThemeState>()((set) => ({
  // Seeded from the same two sources the pre-paint script read, so the store agrees with
  // the class already on the element instead of overwriting it on mount.
  preference: typeof window === 'undefined' ? 'system' : readStoredPreference(),
  systemPrefersDark: readSystemPrefersDark(),
  setPreference: (preference) => set({ preference }),
  setSystemPrefersDark: (systemPrefersDark) => set({ systemPrefersDark }),
}));

/** Narrow selector: a component that only renders the current theme should subscribe to this. */
export function selectThemeClass(state: ThemeState): ThemeClass {
  return resolveThemeClass(state.preference, state.systemPrefersDark);
}

/**
 * Wire the store to the document: apply the class on every change, persist the preference,
 * and follow the OS while the preference is `system`.
 *
 * Returns a teardown function. Called once from `main.tsx`; the teardown exists so a test
 * (and hot-module replacement) can detach the listener.
 */
export function startThemeSync(): () => void {
  const root = document.documentElement;

  const applyThemeClass = (themeClass: ThemeClass): void => {
    root.classList.remove(...ALL_THEME_CLASSES);
    root.classList.add(themeClass);
  };

  // The class only. The pre-paint script has already laid any remembered brand over it, and those
  // values belong to the class it chose, which is this one — see `themeOverrides.ts`.
  applyThemeClass(selectThemeClass(useThemeStore.getState()));

  let lastPersisted = useThemeStore.getState().preference;
  const unsubscribe = useThemeStore.subscribe((state) => {
    applyThemeClass(selectThemeClass(state));
    // In the same listener as the class, so no frame shows one theme's class under the other
    // theme's brand values.
    paintThemeOverrides(root);
    if (state.preference !== lastPersisted) {
      lastPersisted = state.preference;
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, state.preference);
      } catch {
        // A trainer who cannot persist the preference still gets it for this session.
      }
    }
  });

  const media =
    typeof window.matchMedia === 'function' ? window.matchMedia(DARK_MEDIA_QUERY) : null;
  const onSystemChange = (event: MediaQueryListEvent): void => {
    useThemeStore.getState().setSystemPrefersDark(event.matches);
  };
  media?.addEventListener('change', onSystemChange);

  return () => {
    unsubscribe();
    media?.removeEventListener('change', onSystemChange);
  };
}
